import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  ShoppingBag, 
  Users, 
  MessageSquare, 
  BarChart, 
  ClipboardList,
  Menu,
  X,
  LogOut
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
    { path: '/admin/contracts', label: 'Contracten', icon: <FileText className="h-5 w-5" /> },
    { path: '/admin/orders', label: 'Bestellingen', icon: <ShoppingBag className="h-5 w-5" /> },
    { path: '/admin/questionnaires', label: 'Vragenlijsten', icon: <ClipboardList className="h-5 w-5" /> },
    { path: '/admin/clients', label: 'Klanten', icon: <Users className="h-5 w-5" /> },
    { path: '/admin/messages', label: 'Berichten', icon: <MessageSquare className="h-5 w-5" /> },
    { path: '/admin/reports', label: 'Rapporten', icon: <BarChart className="h-5 w-5" /> },
  ];
  
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row">
      {/* Mobile Sidebar Toggle */}
      <div className="md:hidden bg-white shadow-md p-4 flex justify-between items-center">
        <div className="flex items-center">
          <FileText className="h-6 w-6 text-blue-700 mr-2" />
          <span className="font-bold text-xl text-blue-700">LegalDocs Admin</span>
        </div>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      
      {/* Sidebar */}
      <aside 
        className={`bg-white shadow-md w-64 flex-shrink-0 md:flex flex-col ${
          sidebarOpen ? 'block fixed inset-0 z-50 pt-16' : 'hidden'
        }`}
      >
        <div className="p-4 border-b hidden md:block">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-blue-700 mr-2" />
            <span className="font-bold text-xl text-blue-700">LegalDocs Admin</span>
          </div>
        </div>
        
        <div className="flex-grow overflow-y-auto">
          <nav className="p-4">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center p-2 rounded-md ${
                      location.pathname === item.path
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    {item.icon}
                    <span className="ml-3">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        
        <div className="p-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
            <button 
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-500"
              title="Uitloggen"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <div className="flex-grow flex flex-col">
        <header className="bg-white shadow-sm p-4 hidden md:block">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-semibold text-gray-800">
              {navItems.find(item => item.path === location.pathname)?.label || 'Beheer'}
            </h1>
            <div className="flex items-center space-x-4">
              <Link to="/admin/messages" className="text-gray-600 hover:text-blue-700">
                <MessageSquare className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </header>
        
        <main className="flex-grow p-4 md:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;